if (!customElements.get('inline-search')) {
  const InlineSearch = class extends HTMLElement {
    connectedCallback() {
      this.searchInput = this.querySelector('.inline-search__input');
      this.resultsBox = this.querySelector('.inline-search__results');
      this.closeBtn = this.querySelector('.inline-search__close');
      if (!this.searchInput || !this.resultsBox) return;

      this.resultLimit = 8;
      this.searchTimeoutThrottle = 500;
      this.includeMeta = this.dataset.quickSearchMeta === 'true';
      this.searchTimeoutID = -1;
      this.searchAbortController = null;
      this.oldSearchValue = '';

      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handleBlur = this.handleBlur.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);

      this.listenerAbortController = new AbortController();
      const { signal } = this.listenerAbortController;

      this.searchInput.addEventListener('input', this.handleInputChange, { signal });
      this.searchInput.addEventListener('focus', this.handleFocus, { signal });
      this.searchInput.addEventListener('blur', this.handleBlur, { signal });
      this.closeBtn?.addEventListener('click', this.handleCloseClick, { signal });
    }

    disconnectedCallback() {
      this.listenerAbortController?.abort();
    }

    handleFocus() {
      this.classList.add('inline-search--active');
    }

    handleBlur() {
      this.classList.remove('inline-search--active');
    }

    handleCloseClick(evt) {
      evt.preventDefault();
      this.clearResults();
      this.classList.remove('inline-search--active');
    }

    clearResults() {
      this.searchInput.value = '';
      this.oldSearchValue = '';
      if (this.searchAbortController) {
        this.searchAbortController.abort('cancelled');
        this.searchAbortController = null;
      }
      clearTimeout(this.searchTimeoutID);
      this.classList.remove(
        'inline-search--loading',
        'inline-search--has-results',
        'inline-search--no-results',
        'inline-search--has-value',
      );
      this.resultsBox.innerHTML = '';
    }

    handleInputChange() {
      const valueToSearch = this.searchInput.value;
      this.classList.toggle('inline-search--has-value', valueToSearch.length > 0);

      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        this.oldSearchValue = valueToSearch;

        if (this.searchAbortController) {
          this.searchAbortController.abort('Existing request not needed');
          this.searchAbortController = null;
        }
        clearTimeout(this.searchTimeoutID);

        const form = this.searchInput.closest('form');

        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));

        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });
        this.classList.remove('inline-search--has-results', 'inline-search--no-results');
        this.classList.add('inline-search--loading');
        if (!this.resultsBox.querySelector('.inline-search__spinner')) {
          this.resultsBox.innerHTML = '<div class="inline-search__spinner"><div class="loading-spinner"></div></div>';
        }
        this.searchTimeoutID = setTimeout(() => {
          this.fetchResults(valueToSearch, linkURL);
        }, this.searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        this.clearResults();
      }
    }

    fetchResults(valueToSearch, linkURL) {
      this.searchAbortController = new AbortController();
      let ajaxUrl;

      if (theme.Shopify.features.predictiveSearch) {
        ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
        ajaxUrl.searchParams.set('q', valueToSearch);
        ajaxUrl.searchParams.set('section_id', 'predictive-search');
        ajaxUrl.searchParams.set('resources[limit]', this.resultLimit);
        ajaxUrl.searchParams.set(
          'resources[options][fields]',
          this.includeMeta
            ? 'title,product_type,variants.title,vendor,tag,variants.sku'
            : 'title,product_type,variants.title,vendor',
        );
      } else {
        ajaxUrl = new URL(linkURL);
        ajaxUrl.searchParams.set('section_id', 'inline-search');
      }
      fetch(ajaxUrl, { method: 'get', signal: this.searchAbortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          return response.text();
        })
        .then((response) => {
          const template = document.createElement('template');
          template.innerHTML = response;
          const resultsList = theme.Shopify.features.predictiveSearch
            ? template.content.querySelector('.product-grid')
            : template.content.querySelector('.section-search-template .product-grid');

          this.searchAbortController = null;
          this.classList.remove('inline-search--has-results', 'inline-search--no-results', 'inline-search--loading');
          this.resultsBox.innerHTML = '';

          const resultsProducts = document.createElement('div');
          resultsProducts.className = 'inline-search__products collection-listing';
          resultsProducts.innerHTML = '<div></div>';
          const resultsPages = document.createElement('div');
          resultsPages.className = 'inline-search__pages';

          if (resultsList) {
            resultsProducts.firstElementChild.className = resultsList.className;

            resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
              if (index < this.resultLimit) {
                block.classList.add('main-search-result');
                block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                resultsProducts.firstElementChild.appendChild(block);
              }
            });

            resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
              const item = document.createElement('a');
              item.className = 'main-search-result main-search-result--page';
              item.href = block.querySelector('a').href;
              item.innerHTML = '<div class="main-search-result__text"></div>';
              item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
              resultsPages.appendChild(item);
            });
          }

          const areProducts = !!resultsProducts.querySelector('.main-search-result');
          const arePages = !!resultsPages.querySelector('.main-search-result');
          if (areProducts || arePages) {
            this.classList.add('inline-search--has-results');
            if (areProducts) this.resultsBox.appendChild(resultsProducts);
            if (arePages) {
              const heading = document.createElement('h6');
              heading.className = 'main-search-result__heading';
              heading.innerHTML = theme.strings.generalSearchPages;
              resultsPages.insertAdjacentElement('afterbegin', heading);
              this.resultsBox.appendChild(resultsPages);
            }

            const allLink = document.createElement('a');
            allLink.className = 'inline-search__view-all btn btn--secondary';
            allLink.href = linkURL;
            allLink.innerHTML = theme.strings.generalSearchViewAll;
            this.resultsBox.appendChild(allLink);
          } else {
            this.classList.add('inline-search--no-results');
            const emptyMessage = document.createElement('div');
            emptyMessage.className = 'inline-search__empty-message';
            emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
            this.resultsBox.appendChild(emptyMessage);
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.warn(error);
        });
    }
  };

  window.customElements.define('inline-search', InlineSearch);
}
